import { useState, useEffect } from "react";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/Bits";
import { FilePdf, CheckCircle, WarningCircle, ClockCounterClockwise } from "@phosphor-icons/react";

export default function BalanceCheckModal({ open, data, rows, onClose, onCommitted }) {
  const [committing, setCommitting] = useState(false);
  const [pdfing, setPdfing] = useState(false);
  const [current, setCurrent] = useState(data);
  const [asAt, setAsAt] = useState(data?.org?.as_at || new Date().toISOString().slice(0, 10));
  const [rechecking, setRechecking] = useState(false);
  const [postingMode, setPostingMode] = useState("from_today");
  const [result, setResult] = useState(null);
  const [posting, setPosting] = useState(false);
  useEffect(() => { setCurrent(data); setAsAt(data?.org?.as_at || new Date().toISOString().slice(0, 10)); setResult(null); }, [data]);
  if (!current) return null;

  const { org, checks = [], errors = [], summary = {}, valid_rows = 0 } = current;
  const ccy = org?.currency || "GBP";
  const errMap = Object.fromEntries(errors.map((e) => [e.row, e.error]));
  const todayStr = new Date().toISOString().slice(0, 10);
  const balanceDateEarlier = asAt < todayStr;

  const recheck = async (newDate) => {
    setRechecking(true);
    try {
      const { data: res } = await api.post("/import-sheet/check", { rows, tenant_id: org?.tenant_id, as_at: newDate });
      setCurrent(res);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not refresh the check");
    } finally { setRechecking(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data: res } = await api.post("/import-sheet/upload", {
        rows, tenant_id: org?.tenant_id, as_at: asAt,
        posting_mode: balanceDateEarlier ? postingMode : "from_today",
      });
      if (res.created) toast.success(`Created ${res.created} opening-balance schedule(s)`);
      if (res.errors?.length) toast.warning(`${res.errors.length} row(s) skipped`);
      if (res.catch_up_count > 0) { setResult(res); }
      else { onCommitted?.({ created: res.created }); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setCommitting(false); }
  };

  const postCatchUp = async () => {
    setPosting(true);
    try {
      const { data: r } = await api.post("/scheduler/run");
      if (r.queued) toast.success(`${r.posted} posted, ${r.queued} sent for approval`);
      else toast.success(`${r.posted} catch-up posting(s) posted to Xero`);
      onCommitted?.({ created: result?.created });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post catch-up journals");
    } finally { setPosting(false); }
  };

  const downloadPdf = async () => {
    setPdfing(true);
    try {
      const res = await api.post("/import-sheet/check/pdf", { rows, tenant_id: org?.tenant_id, as_at: asAt }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `opening-balance-check-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Balance-check report downloaded");
    } catch { toast.error("Could not generate report"); }
    finally { setPdfing(false); }
  };

  if (result) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && (onCommitted?.({ created: result?.created }))}>
        <DialogContent className="max-w-lg rounded-lg border-slate-200" data-testid="catchup-result-modal">
          <DialogHeader>
            <div className="w-11 h-11 rounded-xl bg-orange/12 flex items-center justify-center mb-2">
              <ClockCounterClockwise size={22} className="text-orangeink" weight="fill" />
            </div>
            <DialogTitle className="font-heading font-bold text-2xl tracking-tight text-navy">
              Import complete — historical postings to run
            </DialogTitle>
            <DialogDescription className="text-navy/70">
              {result.created} schedule(s) imported. <b>{result.catch_up_count} catch-up posting(s)</b>
              {" "}({money(result.catch_up_total, ccy)}) dated between <b>{asAt}</b> and today need to be posted
              to Xero to bring the ledger up to date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {result.locked_skipped > 0 && (
              <div className="text-sm text-warn bg-warn/5 border border-warn/20 rounded-lg px-3 py-2" data-testid="catchup-locked">
                {result.locked_skipped} period(s) fell on/before your lock date ({result.lock_date}) and were treated as opening (Xero would reject them).
              </div>
            )}
            <div className="text-sm text-navy/70 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
              These appear on the <b>Month-end Close</b> page as overdue postings (and under <b>Approvals</b> if 2-person approval is on). Post them now, or run them there later.
            </div>
            <div className="flex justify-end gap-3 pt-1">
              <button onClick={() => onCommitted?.({ created: result?.created })} data-testid="catchup-later"
                className="px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
                I'll do it later
              </button>
              <button onClick={postCatchUp} disabled={posting} data-testid="catchup-post-now"
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
                <ClockCounterClockwise size={16} /> {posting ? "POSTING…" : "Post catch-up now"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[96vw] max-w-[1400px] rounded-lg border-slate-200 p-0 gap-0 max-h-[92vh] overflow-y-auto" data-testid="balance-check-modal">
        <DialogHeader className="px-7 pt-6 pb-4 border-b border-slate-100">
          <div className="overline text-orangeink mb-1">Balance check · before commit</div>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tighter text-navy">
            {org?.tenant_name || "Opening balances"}
          </DialogTitle>
          <DialogDescription className="text-navy/70">
            Review how each nominal code in your sheet ties back to its Xero balance, then commit the import.
          </DialogDescription>
        </DialogHeader>

        <div className="px-7 py-4 flex flex-wrap items-center gap-3 border-b border-slate-100 bg-navy/[0.02]">
          <Stat label="Valid rows" value={valid_rows} />
          <Stat label="Codes" value={summary.codes || 0} />
          <Stat label="Tie" value={summary.ties || 0} tone="pos" />
          <Stat label="Variance" value={summary.variances || 0} tone={summary.variances ? "neg" : ""} />
          <Stat label="Sheet total" value={money(summary.sheet_total, ccy)} />
          <div className="ml-auto flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
            <span className="overline text-navy/65 whitespace-nowrap">Xero balance as at</span>
            <input type="date" value={asAt} data-testid="bc-as-at-date"
              onChange={(e) => { setAsAt(e.target.value); recheck(e.target.value); }}
              className="text-sm font-mono text-navy outline-none bg-transparent" />
            {rechecking && <span className="text-xs text-orangeink font-mono">…</span>}
          </div>
        </div>

        {!org?.live && (
          <div className="px-7 py-3 flex items-center gap-2 text-sm text-warn bg-warn/5 border-b border-warn/20" data-testid="bc-no-live">
            <WarningCircle size={18} /> Not connected to live Xero — Xero balances aren't available, so codes can't be auto-reconciled.
          </div>
        )}

        {balanceDateEarlier && (
          <div className="px-7 py-4 border-b border-slate-100 bg-orange/[0.03]" data-testid="posting-mode-choice">
            <div className="flex items-start gap-2 mb-3">
              <ClockCounterClockwise size={18} className="text-orangeink mt-0.5 shrink-0" weight="fill" />
              <div className="text-sm text-navy/75">
                Your Xero balance is dated <b>{asAt}</b>, earlier than today. How should releases between then and now be handled?
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <label className={`flex gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${postingMode === "from_today" ? "border-orange bg-white ring-1 ring-orange/30" : "border-slate-200 bg-white/60 hover:border-slate-300"}`}
                data-testid="mode-from-today">
                <input type="radio" name="pmode" checked={postingMode === "from_today"} onChange={() => setPostingMode("from_today")} className="mt-1 accent-orange" />
                <span className="text-sm">
                  <span className="font-semibold text-navy">Post from today</span>
                  <span className="block text-xs text-navy/70 mt-0.5">Xero is already up to date — only future periods post going forward.</span>
                </span>
              </label>
              <label className={`flex gap-2.5 p-3 rounded-lg border cursor-pointer transition-colors ${postingMode === "from_balance_date" ? "border-orange bg-white ring-1 ring-orange/30" : "border-slate-200 bg-white/60 hover:border-slate-300"}`}
                data-testid="mode-from-balance-date">
                <input type="radio" name="pmode" checked={postingMode === "from_balance_date"} onChange={() => setPostingMode("from_balance_date")} className="mt-1 accent-orange" />
                <span className="text-sm">
                  <span className="font-semibold text-navy">Catch up from {asAt}</span>
                  <span className="block text-xs text-navy/70 mt-0.5">Post the missing releases from the balance date to today (each on its own month-end).</span>
                </span>
              </label>
            </div>
          </div>
        )}

        <div className="px-7 pt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-navy/65">Lines to import ({rows?.length || 0})</div>
            {errors.length > 0 && (
              <div className="text-xs font-medium text-negative">{errors.length} with issues</div>
            )}
          </div>
          <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-64 overflow-y-auto" data-testid="import-lines">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="border-b border-slate-200 overline text-navy/65">
                  <Th>#</Th><Th>Type</Th><Th>Code</Th><Th>Description</Th>
                  <Th>Period</Th><Th>Freq</Th><Th right>Amount</Th><Th />
                </tr>
              </thead>
              <tbody>
                {(rows || []).map((r, i) => {
                  const err = errMap[i + 1];
                  return (
                    <tr key={i} data-testid={`import-line-${i + 1}`}
                      className={`border-b border-slate-100 last:border-0 ${err ? "bg-negative/[0.04]" : ""}`}>
                      <Td mono className="text-navy/65">{i + 1}</Td>
                      <Td>{r.category || "—"}</Td>
                      <Td mono className="text-navy/70">{r.source_account_code || "—"}{r.pl_account_code ? ` → ${r.pl_account_code}` : ""}</Td>
                      <Td className="max-w-[460px] truncate" title={r.description}>{r.description || <span className="text-navy/65">—</span>}
                        {r.reference ? <div className="text-xs text-navy/65 font-mono">{r.reference}</div> : null}</Td>
                      <Td mono className="text-navy/70 whitespace-nowrap text-xs">{r.start_date || "?"} → {r.end_date || "?"}</Td>
                      <Td className="text-xs">{r.frequency || "—"}</Td>
                      <Td right mono className="font-semibold text-navy">{money(r.original_amount, ccy)}
                        {r.remaining_amount != null && (
                          <div className="text-xs text-orangeink font-medium" title="Remaining balance at import date">
                            {money(r.remaining_amount, ccy)} rem.
                          </div>
                        )}</Td>
                      <Td>{err
                        ? <span className="inline-flex items-center gap-1 text-xs text-negative font-medium" title={err}><WarningCircle size={14} weight="fill" /> Skip</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-positive font-medium"><CheckCircle size={14} weight="fill" /> OK</span>}</Td>
                    </tr>
                  );
                })}
                {(rows || []).length === 0 && (
                  <tr><td colSpan={8} className="py-6 text-center text-navy/70">No lines parsed from the file.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-7 py-5">
          <div className="overline text-navy/65 mb-2">Balance check by nominal code</div>
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-slate-200 overline text-navy/65">
                  <Th>Code</Th><Th>Account</Th><Th right>Sheet total</Th><Th right>Existing</Th>
                  <Th right>Projected</Th><Th right>Xero balance</Th><Th right>Variance</Th><Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {checks.map((c) => (
                  <tr key={c.code} data-testid={`bc-row-${c.code}`} className="border-b border-slate-100">
                    <Td mono className="font-semibold text-navy">{c.code}</Td>
                    <Td>{c.account_name || <span className="text-navy/65">—</span>}
                      <div className="text-xs text-navy/65">{c.rows} row{c.rows !== 1 ? "s" : ""}</div></Td>
                    <Td right mono>{money(c.sheet_total, ccy)}</Td>
                    <Td right mono className="text-navy/70">{money(c.existing_remaining, ccy)}</Td>
                    <Td right mono className="font-semibold">{money(c.projected_total, ccy)}</Td>
                    <Td right mono>{c.xero_balance != null ? money(c.xero_balance, ccy) : "—"}</Td>
                    <Td right mono className={c.status === "Variance" ? "text-negative font-semibold" : "text-navy/70"}>
                      {c.variance != null ? money(c.variance, ccy) : "—"}</Td>
                    <Td><StatusBadge value={c.status} testid={`bc-status-${c.code}`} /></Td>
                  </tr>
                ))}
                {checks.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center text-navy/70" data-testid="bc-empty">No valid coded rows to check.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {errors.length > 0 && (
            <div className="mt-4 bg-negative/5 border border-negative/25 rounded-lg p-4" data-testid="bc-errors">
              <div className="text-sm font-semibold text-negative mb-2">{errors.length} row(s) will be skipped on import</div>
              <ul className="text-xs text-negative/90 space-y-1 max-h-32 overflow-y-auto font-mono">
                {errors.slice(0, 50).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-7 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={downloadPdf} disabled={pdfing} data-testid="bc-download-pdf"
            className="flex items-center gap-2 px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:border-navy hover:bg-navy/5 transition-colors disabled:opacity-60">
            <FilePdf size={18} /> {pdfing ? "BUILDING…" : "Download PDF report"}
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} data-testid="bc-cancel"
              className="px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
            <button onClick={commit} disabled={committing || valid_rows === 0} data-testid="bc-commit"
              className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
              <CheckCircle size={18} weight="fill" /> {committing ? "IMPORTING…" : `Commit import (${valid_rows})`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone }) {
  const color = tone === "pos" ? "text-positive" : tone === "neg" ? "text-negative" : "text-navy";
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-2 min-w-[110px]">
      <div className="overline text-navy/65">{label}</div>
      <div className={`font-mono font-semibold tabular ${color}`}>{value}</div>
    </div>
  );
}

const Th = ({ children, right }) => <th className={`px-4 py-2.5 ${right ? "text-right" : "text-left"} font-semibold whitespace-nowrap`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) =>
  <td className={`px-4 py-3 ${right ? "text-right" : "text-left"} ${mono ? "font-mono tabular" : ""} ${className}`}>{children}</td>;
